import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Briefcase, Scale, BookOpen, Award, Heart, Target, Eye, Lightbulb, Shield, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';

const WorkWithUs = () => {
  const team = [
    {
      role: 'Legal Consultants',
      icon: Scale,
      description: 'Experienced attorneys and legal professionals providing expert guidance on Philippine laws, regulations, and compliance matters.',
      skills: ['Legal Research', 'Policy Analysis', 'Regulatory Compliance', 'Contract Review']
    },
    {
      role: 'Policy Analysts',
      icon: BookOpen,
      description: 'Specialists in legislative drafting, policy development, and governance frameworks for public and private institutions.',
      skills: ['Legislative Drafting', 'Policy Development', 'Impact Assessment', 'Stakeholder Engagement']
    },
    {
      role: 'Technology Partners',
      icon: Briefcase,
      description: 'Tech professionals building intelligent tools and platforms that make legal resources more accessible and efficient.',
      skills: ['Legal Tech Development', 'AI Integration', 'Platform Architecture', 'User Experience']
    },
    {
      role: 'Content Creators',
      icon: Award,
      description: 'Writers, researchers, and educators developing high-quality legal content, eBooks, and educational materials.',
      skills: ['Legal Writing', 'Research', 'Educational Content', 'Publications']
    }
  ];

  // STOIC Core Values
  const stoicValues = [
    {
      letter: 'S',
      title: 'Service',
      description: 'Making legal clarity accessible to those who need it most, championing inclusive and democratized legal knowledge for all.',
      icon: Heart
    },
    {
      letter: 'T',
      title: 'Teamwork',
      description: 'Working closely with professionals, institutions, and communities to co-create solutions and share responsibility for meaningful, sustainable outcomes.',
      icon: Users
    },
    {
      letter: 'O',
      title: 'Outstanding',
      description: 'Delivering innovative, quality resources and services that exceed expectations, set high standards, and continuously improve how legal support is delivered.',
      icon: Award
    },
    {
      letter: 'I',
      title: 'Integrity',
      description: 'Upholding the highest ethical standards in everything we do, ensuring trust, transparency, and accountability in all our dealings.',
      icon: Shield
    },
    {
      letter: 'C',
      title: 'Collaboration',
      description: 'Building enduring partnerships that leverage diverse expertise, foster innovation, and create lasting impact in the pursuit of accessible justice.',
      icon: Handshake
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="py-12 bg-[#F9F9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            Who We Are
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            A team of dedicated professionals committed to making legal clarity accessible to everyone
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center text-[#8B1818] hover:text-[#701313] mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        {/* Our Story Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
            Our Story
          </h2>
          <div className="prose prose-lg max-w-none text-slate-600 space-y-4">
            <p>
              <span className="text-[#8B1818] font-semibold">Lex</span>DoRight was born from a simple yet powerful belief: 
              that everyone deserves access to clear, understandable, and actionable legal knowledge. Too often, 
              the complexity of legal language creates barriers that prevent individuals and businesses from making 
              informed decisions.
            </p>
            <p>
              Founded by a team of legal practitioners, policy experts, and technology enthusiasts, we set out to 
              bridge the gap between complex legal frameworks and practical, everyday solutions. Our name reflects 
              our commitment: "Lex" (law) and "Do Right," guiding people to understand and apply the law correctly.
            </p>
            <p>
              Today, <span className="text-[#8B1818] font-semibold">Lex</span>DoRight stands as a comprehensive platform offering intelligent legal tools, expert 
              consultation services, educational resources, and corporate solutions. While we have a special focus 
              on serving Filipinos and Philippine-based institutions, our resources and expertise extend to clients 
              worldwide who seek clarity in legal and governance matters.
            </p>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-gradient-to-br from-[#8B1818] to-[#701313] rounded-2xl p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <Target className="h-8 w-8 text-[#FDB913]" />
              <h2 className="text-2xl font-bold">Our Mission</h2>
            </div>
            <p className="text-white/90 text-lg leading-relaxed">
              To democratize legal knowledge by providing accessible, reliable, and innovative legal resources 
              and services that empower individuals and organizations to make informed, ethical, and compliant 
              decisions in their personal and professional lives, with a special commitment to serving the Filipino community.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-2xl p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <Eye className="h-8 w-8 text-[#FDB913]" />
              <h2 className="text-2xl font-bold">Our Vision</h2>
            </div>
            <p className="text-white/90 text-lg leading-relaxed">
              To be a globally trusted platform for legal clarity, where every individual, business, 
              and institution can confidently navigate the legal landscape with the support of cutting-edge 
              technology and expert guidance, with the Philippines as our home and heart.
            </p>
          </div>
        </div>

        {/* STOIC Core Values */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              Our Core Values
            </h2>
            <div className="flex items-center justify-center gap-2 mb-3">
              {['S', 'T', 'O', 'I', 'C'].map((letter) => (
                <span 
                  key={letter}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C9A227] text-white font-bold text-xl shadow-sm"
                  style={{fontFamily: 'Playfair Display, serif'}}
                >
                  {letter}
                </span>
              ))}
            </div>
            <p className="text-slate-600">
              Guided by <span className="text-[#C9A227] font-semibold">STOIC</span> principles in everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stoicValues.map((value, index) => (
              <div 
                key={value.title}
                className={`bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow ${index === 4 ? 'md:col-span-2 lg:col-span-1 lg:mx-auto lg:w-full' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-[#FDB913]/20 flex items-center justify-center">
                      <value.icon className="h-6 w-6 text-[#8B1818]" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      <span className="text-[#C9A227]">{value.letter}</span>
                      <span className="text-slate-900">{value.title.slice(1)}</span>
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Our Professionals */}
        <h2 className="text-2xl font-bold text-slate-900 mb-8" style={{fontFamily: 'Playfair Display, serif'}}>
          Our Professionals
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {team.map((member, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-[#8B1818]/10 p-3 rounded-lg">
                  <member.icon className="h-6 w-6 text-[#8B1818]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{member.role}</h3>
              </div>
              <p className="text-slate-600 mb-4">{member.description}</p>
              <div className="flex flex-wrap gap-2">
                {member.skills.map((skill, idx) => (
                  <span key={idx} className="px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* What We Offer */}
        <div className="bg-slate-100 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
            What We Offer
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <Scale className="h-8 w-8 text-[#8B1818]" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Legal Consultation</h3>
              <p className="text-sm text-slate-600">Expert guidance on Philippine laws and regulations</p>
            </div>
            <div className="text-center">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <Lightbulb className="h-8 w-8 text-[#8B1818]" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Intelligent Tools</h3>
              <p className="text-sm text-slate-600">AI-powered applications for legal workflows</p>
            </div>
            <div className="text-center">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <BookOpen className="h-8 w-8 text-[#8B1818]" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Educational Resources</h3>
              <p className="text-sm text-slate-600">eBooks, guides, and learning materials</p>
            </div>
            <div className="text-center">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <Briefcase className="h-8 w-8 text-[#8B1818]" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Corporate Solutions</h3>
              <p className="text-sm text-slate-600">Tailored services for businesses and institutions</p>
            </div>
          </div>
        </div>

        {/* Join Our Team Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center gap-4 mb-6">
            <Users className="h-8 w-8 text-[#8B1818]" />
            <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
              Join Our Team
            </h2>
          </div>
          
          <p className="text-slate-600 mb-6">
            We are always looking for passionate individuals who share our vision of making legal knowledge accessible. 
            Whether you're a legal professional, tech enthusiast, content creator, or policy expert, we'd love to hear from you.
          </p>

          <div className="bg-slate-50 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-slate-900 mb-3">What We Look For:</h3>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#8B1818] rounded-full"></span>
                Expertise in Philippine law, policy, or governance
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#8B1818] rounded-full"></span>
                Passion for legal technology and innovation
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#8B1818] rounded-full"></span>
                Strong communication and writing skills
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#8B1818] rounded-full"></span>
                Commitment to making legal resources accessible
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact">
              <Button className="bg-[#8B1818] text-white hover:bg-[#701313]">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkWithUs;
